import { useMemo } from 'react';

// Curated palette (sufficient contrast on light + dark backgrounds)
const AVATAR_PALETTE = [
  { bg: '#0891b2', fg: '#ffffff' }, // cyan-600
  { bg: '#0d9488', fg: '#ffffff' }, // teal-600
  { bg: '#059669', fg: '#ffffff' }, // emerald-600
  { bg: '#4f46e5', fg: '#ffffff' }, // indigo-600
  { bg: '#7c3aed', fg: '#ffffff' }, // violet-600
  { bg: '#c026d3', fg: '#ffffff' }, // fuchsia-600
  { bg: '#db2777', fg: '#ffffff' }, // pink-600
  { bg: '#e11d48', fg: '#ffffff' }, // rose-600
  { bg: '#ea580c', fg: '#ffffff' }, // orange-600
  { bg: '#ca8a04', fg: '#ffffff' }, // yellow-600
  { bg: '#65a30d', fg: '#ffffff' }, // lime-600
  { bg: '#475569', fg: '#ffffff' }, // slate-600
];

const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

export const getInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const SIZE_MAP = {
  xs: { box: 18, font: 9, ring: 1 },
  sm: { box: 24, font: 10, ring: 1.5 },
  md: { box: 32, font: 12, ring: 2 },
  lg: { box: 40, font: 14, ring: 2 },
  xl: { box: 56, font: 20, ring: 2.5 },
};

/**
 * Reusable circular user avatar.
 * Renders user's `picture` when present, otherwise deterministic initials on a colored circle.
 *
 * Props:
 * - user: { name, picture?, user_id?, email? }
 * - size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'  (default 'sm')
 * - ring: boolean — show a subtle ring (default false)
 * - ringColor: string — override ring color
 * - title: string — override tooltip (default user.name)
 * - className: string
 */
export const UserAvatar = ({
  user,
  size = 'sm',
  ring = false,
  ringColor = '#ffffff',
  title,
  className = '',
  'data-testid': testId,
}) => {
  const { box, font, ring: ringWidth } = SIZE_MAP[size] || SIZE_MAP.sm;

  const palette = useMemo(() => {
    const seed = user?.user_id || user?.email || user?.name || '?';
    return AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length];
  }, [user?.user_id, user?.email, user?.name]);

  const initials = getInitials(user?.name);
  const displayTitle = title || user?.name || 'User';

  const wrapperStyle = ring
    ? { padding: ringWidth, backgroundColor: ringColor, borderRadius: '9999px', display: 'inline-block' }
    : { display: 'inline-block' };

  const avatarStyle = {
    width: box,
    height: box,
    borderRadius: '9999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: font,
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: 1,
    userSelect: 'none',
    overflow: 'hidden',
    color: palette.fg,
    backgroundColor: palette.bg,
  };

  return (
    <span style={wrapperStyle} className={className} title={displayTitle} data-testid={testId}>
      {user?.picture ? (
        <img
          src={user.picture}
          alt={displayTitle}
          style={{ ...avatarStyle, objectFit: 'cover' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline-flex'; }}
        />
      ) : null}
      <span style={{ ...avatarStyle, display: user?.picture ? 'none' : 'inline-flex' }}>
        {initials}
      </span>
    </span>
  );
};

/**
 * Stacked row of avatars with overflow "+N" badge.
 *
 * Props:
 * - users: array of user objects
 * - size: avatar size
 * - max: maximum shown before collapsing into +N (default 5)
 */
export const AvatarStack = ({ users = [], size = 'sm', max = 5, className = '', 'data-testid': testId }) => {
  if (!users.length) return null;
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;
  const { box } = SIZE_MAP[size] || SIZE_MAP.sm;
  const overlap = Math.round(box * 0.35);

  return (
    <span className={`inline-flex items-center ${className}`} data-testid={testId}>
      {shown.map((u, i) => (
        <span
          key={u.user_id || u.email || u.name || i}
          style={{ marginLeft: i === 0 ? 0 : -overlap, position: 'relative', zIndex: shown.length - i }}
        >
          <UserAvatar user={u} size={size} ring />
        </span>
      ))}
      {overflow > 0 && (
        <span
          style={{
            marginLeft: -overlap,
            width: box,
            height: box,
            borderRadius: '9999px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: SIZE_MAP[size].font,
            fontWeight: 600,
            backgroundColor: '#e5e7eb',
            color: '#374151',
            border: '2px solid #fff',
          }}
          title={`${overflow} more`}
          data-testid={`${testId || 'avatar-stack'}-overflow`}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
};

export default UserAvatar;
